import { useDotBotsAuth } from '@dotbots-boutique/auth-sdk/react';
import { useCallback } from 'react';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;

export type ApiInit = Omit<RequestInit, 'body'> & { body?: unknown };

// Use auth.fetch() with absolute URL — the SDK detects absolute URLs and only
// adds auth headers, leaving the URL untouched. Never use a relative path.
export function useApi() {
  const { fetch: authFetch } = useDotBotsAuth();

  return useCallback(
    async <T = unknown>(path: string, init: ApiInit = {}): Promise<T> => {
      const url = `${BACKEND_URL}${path}`;
      const headers = new Headers(init.headers ?? {});

      // Strip our `unknown` body before constructing the RequestInit — we set
      // a properly-typed body below.
      const { body: rawBody, headers: _h, ...rest } = init;
      const opts: RequestInit = { ...rest, headers };

      if (rawBody !== undefined) {
        if (rawBody instanceof FormData) {
          opts.body = rawBody;
        } else if (typeof rawBody === 'string') {
          opts.body = rawBody;
        } else {
          if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
          opts.body = JSON.stringify(rawBody);
        }
      }

      const response = await authFetch(url, opts);
      const text = await response.text();
      const json = text ? safeJson(text) : null;

      if (!response.ok) {
        const error = new ApiError(
          (json as { error?: string })?.error ?? `http_${response.status}`,
          response.status,
          json,
        );
        throw error;
      }
      return json as T;
    },
    [authFetch],
  );
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}
